class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",

      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };

    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };

    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType = null; // "paginate" | "loadmore" | "infinite"
    this.totalPages = 1;
    this.totalProducts = 0;
    this.infiniteLoadingObserver = null;

    this.showColSwitchers = false;
    this.cachedCol = null;
    this.initialCol = null;
    this.activeCol = null;
    this.STORAGE_KEY = "gridColumnViews";

    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters = false;
    this.enableSorting = false;
    this.filtersType = null; // "storefront_filters" | "tags_filter"
    this.filtersPosition = null; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions = null;
    this.sideEffectEventsAdded = false;

    this.isLoadingMore = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);

    if (!this.domNodes || !this.domNodes.section) return;

    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();

    if (!this.sideEffectEventsAdded) {
      MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());

      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
      document.addEventListener("matchMobile", () => this.handleStickyToolbar());
      document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());

      this.sideEffectEventsAdded = true;
    }

    this.handleStickyToolbar();
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;

    this.sectionId = section.dataset.sectionId || "";
    this.paginationType = section.dataset.paginationType || "paginate";
    this.view = section.dataset.view || "";

    this.totalPages = productContainer ? Number(productContainer.dataset.totalPages || 1) : 1;
    this.totalProducts = Number(section.dataset.productCount || 0);

    this.activeCol = null;
    this.currentPage = this.getCurrentPageFromUrl();

    this.initialCol = Number(section.dataset.initialColumn || 4);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";

    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType || "";
    this.filtersPosition = section.dataset.filtersPosition || "";
  };

  getCurrentPageFromUrl = () => {
    const page = Number(new URL(window.location.href).searchParams.get("page"));
    return page && page > 0 ? page : 1;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    this.initLoadMore();

    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
  };

  initFilters = () => {
    if (!this.enableFilters) return;

    const { sidebar, openSidebar, closeSidebar } = this.domNodes;

    if (!sidebar || !openSidebar || !closeSidebar) return;

    window.requestAnimationFrame(this.initAccordions);

    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (e) => {
      if (e.target === sidebar) this.closeSidebarFilter();
    });
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;

    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;

    if (!openSorting || !closeSorting || !sortingWrapper) return;

    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => {
      if (e.target === sortingWrapper) this.closeMobileSorting();
    });
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;

    if (!sortingWrapper || !sortingList) return;

    sortingWrapper.style.display = "block";

    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;

    if (!sortingWrapper || !sortingList) return;

    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");

    setTimeout(() => {
      sortingWrapper.style.setProperty("display", "none");
    }, 300);
  };

  initAccordions = () => {
    const { filtersWrapper } = this.domNodes;

    if (!filtersWrapper || !MinimogLibs || !MinimogLibs.Accordion) return;

    this.accordions && this.accordions.destroy();

    filtersWrapper.classList.remove("acc-initialized");

    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty("opacity", "1"),
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;

    if (!sidebar || !sidebarContent || !section) return;

    sidebar.style.display = "block";

    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });

    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;

    if (!sidebar || !sidebarContent || !section) return;

    section.classList.remove("sidebar-open");

    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");

      setTimeout(() => {
        sidebar.style.removeProperty("display");
      }, 300);
    }
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;

    const { cols, productContainer } = this.domNodes;

    if (!productContainer) return;

    const allImages = productContainer.querySelectorAll("responsive-image");

    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        if (typeof img.disconnectedCallback === "function") img.disconnectedCallback();
        if (typeof img.connectedCallback === "function") img.connectedCallback();
      });
    }

    const activeCol = this.activeCol || this.initialCol;

    if (this.showColSwitchers && cols && cols.length) {
      if (cols[activeCol - 1]) cols[activeCol - 1].classList.remove("active");
      if (cols[col - 1]) cols[col - 1].classList.add("active");
    }

    productContainer.classList.remove(`m-cols-${activeCol}`);
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return;

    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];

    if (maxCol < this.activeCol) {
      this.toggleView(maxCol);
    }
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    const { productContainer, loadMoreBtn } = this.domNodes;

    if (this.paginationType === "paginate") return;
    if (this.view === "search") return;
    if (!productContainer || !loadMoreBtn) return;
    if (!this.totalPages || this.totalPages <= this.currentPage) {
      this.removeLoadMoreButton();
      return;
    }

    loadMoreBtn.addEventListener("click", (event) => {
      event.preventDefault();
      this.loadMoreProducts();
    });

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadMoreProducts();
            }
          });
        },
        { threshold: 0.25 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

loadMoreProducts = () => {
  const { productContainer, loadMoreBtn } = this.domNodes;

  if (!productContainer || !loadMoreBtn) return;
  if (this.isLoadingMore) return;

  const targetValidCount = 48;
  let nextPage = this.currentPage + 1;
  let addedCount = 0;

  if (nextPage > this.totalPages) {
    this.removeLoadMoreButton();
    return;
  }

  this.isLoadingMore = true;
  this.toggleLoading(true);

  const fetchUntilFilled = () => {
    if (nextPage > this.totalPages || addedCount >= targetValidCount) {
      if (nextPage > this.totalPages) {
        this.currentPage = this.totalPages;
        this.removeLoadMoreButton();
      }

      this.refreshLoadedProducts();

      this.isLoadingMore = false;
      this.toggleLoading(false);
      return;
    }

    this.fetchCollectionPage(nextPage)
      .then((html) => {
        const fetchedContainer = this.getProductContainerFromHtml(html);

        if (!fetchedContainer) {
          throw new Error("No product container found in fetched HTML.");
        }

        const productNodes = fetchedContainer.querySelectorAll(".m-product-item");

        productNodes.forEach((productNode) => {
          if (addedCount >= targetValidCount) return;

          const card = productNode.querySelector(".m-product-card");

          if (card && card.classList.contains("m-product-card--soldout")) {
            return;
          }

          productContainer.appendChild(productNode);
          addedCount++;
        });

        this.currentPage = nextPage;
        nextPage++;

        if (addedCount < targetValidCount && nextPage <= this.totalPages) {
          fetchUntilFilled();
          return;
        }

        this.refreshLoadedProducts();

        if (this.currentPage >= this.totalPages) {
          this.removeLoadMoreButton();
        }

        this.isLoadingMore = false;
        this.toggleLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load more products:", error);

        this.isLoadingMore = false;
        this.toggleLoading(false);
      });
  };

  fetchUntilFilled();
};

  fetchCollectionPage = (page) => {
    const sectionUrl = this.buildCollectionUrl(page, true);

    return fetch(sectionUrl, {
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Section request failed with status ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        if (this.getProductContainerFromHtml(html)) {
          return html;
        }

        const fullPageUrl = this.buildCollectionUrl(page, false);

        return fetch(fullPageUrl, {
          credentials: "same-origin",
        }).then((response) => {
          if (!response.ok) {
            throw new Error(`Full page fallback failed with status ${response.status}`);
          }

          return response.text();
        });
      });
  };

  buildCollectionUrl = (page, useSectionId) => {
    const url = new URL(window.location.href);

    url.searchParams.set("page", page);

    if (useSectionId && this.sectionId) {
      url.searchParams.set("section_id", this.sectionId);
    } else {
      url.searchParams.delete("section_id");
    }

    return url.toString();
  };

  getProductContainerFromHtml = (html) => {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;

    return wrapper.querySelector("[data-product-container]");
  };

  refreshLoadedProducts = () => {
    if (typeof MinimogEvents !== "undefined") {
      MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
    }

    this.initProducts();

    if (typeof SPR !== "undefined" && SPR.initDomEls && SPR.loadBadges) {
      SPR.initDomEls();
      SPR.loadBadges();
    }
  };

  removeLoadMoreButton = () => {
    const { loadMoreBtn } = this.domNodes;

    if (!loadMoreBtn) return;

    if (this.infiniteLoadingObserver) {
      this.infiniteLoadingObserver.unobserve(loadMoreBtn);
      this.infiniteLoadingObserver.disconnect();
      this.infiniteLoadingObserver = null;
    }

    if (loadMoreBtn.parentNode) {
      loadMoreBtn.parentNode.remove();
    }
  };

  initProducts = () => {
    if (MinimogTheme.CompareProduct) {
      MinimogTheme.CompareProduct.setCompareButtonsState();
    }

    if (MinimogTheme.Wishlist) {
      MinimogTheme.Wishlist.setWishlistButtonsState();
    }
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;

    if (!loadMoreBtn) return;

    const method = loading ? "add" : "remove";
    loadMoreBtn.classList[method]("m-spinner-loading");
  };

  handleStickyToolbar = () => {
    const toolbar = this.domNodes.toolbar;

    if (!toolbar) return;

    const extraSpace = toolbar.offsetTop;
    let lastScroll = 0;

    const onScroll = () => {
      if (!MinimogTheme.config || !MinimogTheme.config.mqlMobile) {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
        return;
      }

      const currentScroll = window.scrollY;

      if (currentScroll <= extraSpace) {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
        return;
      }

      toolbar.classList.add("m-collection-toolbar--sticky");

      if (
        currentScroll > toolbar.offsetHeight + extraSpace &&
        currentScroll > lastScroll &&
        !toolbar.classList.contains("scroll-down")
      ) {
        toolbar.classList.remove("scroll-up");
        toolbar.classList.add("scroll-down");
      } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
        toolbar.classList.remove("scroll-down");
        toolbar.classList.add("scroll-up");
      }

      lastScroll = currentScroll;
    };

    if (!toolbar.dataset.stickyHandlerAdded) {
      window.addEventListener("scroll", onScroll);
      toolbar.dataset.stickyHandlerAdded = "true";
    }
  };
}